import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { typography, spacing, layout } from '../utils/theme';
import { getDailyInsight } from '../utils/dailyInsight';
import { ExplainableMetric } from './MetricExplanationModal';

// This component renders inline content for the DOB Calculator's "Daily Insight" section.
// It does NOT wrap itself in a card — the parent handles the card, sectionHeader, and toggle.
// It only returns the cardContent innards.

const DailyInsight = ({ birthMonth, birthDay, birthYear, colors }) => {
  const styles = useMemo(() => createStyles(colors), [colors]);

  const { insight, error } = useMemo(() => {
    try {
      const m = parseInt(birthMonth);
      const d = parseInt(birthDay);
      const y = parseInt(birthYear);
      if (!m || !d || !y || y < 1900 || y > 2100) return { insight: null, error: null };
      if (m < 1 || m > 12 || d < 1 || d > 31) return { insight: null, error: null };
      const result = getDailyInsight(birthMonth, birthDay, birthYear);
      return { insight: result, error: null };
    } catch (e) {
      console.log('DailyInsight error:', e.message);
      return { insight: null, error: e.message };
    }
  }, [birthMonth, birthDay, birthYear]);

  if (!insight && !error) return null;

  if (error) {
    return (
      <Text style={styles.emptyText}>Daily insight unavailable</Text>
    );
  }

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'auspicious': case 'harmonious': case 'supported': return '#4CAF50';
      case 'inauspicious': case 'challenging': return '#F44336';
      case 'empowered': return '#2196F3';
      case 'giving': return '#FF9800';
      default: return colors.lightText;
    }
  };

  const getBioColor = (value) => {
    if (value > 50) return '#4CAF50';
    if (value > 0) return '#8BC34A';
    if (value > -50) return '#FF9800';
    return '#F44336';
  };

  const renderInfoRow = (label, value, emoji, metricKey) => (
    <ExplainableMetric metricKey={metricKey} style={styles.infoRow} helpColor={colors.lightText}>
      <Text style={styles.infoLabel}>{emoji ? emoji + ' ' : ''}{label}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </ExplainableMetric>
  );

  const renderBioBar = (label, value, metricKey) => {
    const pct = (value + 100) / 2;
    const color = getBioColor(value);
    return (
      <ExplainableMetric metricKey={metricKey} style={styles.bioRow} helpColor={colors.lightText}>
        <Text style={styles.bioLabel}>{label}</Text>
        <View style={styles.bioBarBg}>
          <View style={[styles.bioBarFill, { width: `${pct}%`, backgroundColor: color }]} />
          <View style={styles.bioBarCenter} />
        </View>
        <Text style={[styles.bioValue, { color }]}>{value}%</Text>
      </ExplainableMetric>
    );
  };

  const archetype = insight.dailyArchetype;

  return (
    <View style={styles.content}>
      <ExplainableMetric metricKey="baziTransit" style={styles.explainableLabel} helpColor={colors.lightText}>
        <Text style={styles.subsectionLabel}>Your DOB-Based BaZi Transit</Text>
      </ExplainableMetric>

      <ExplainableMetric metricKey="dailyTheme" style={styles.highlightBox} iconStyle={styles.metricHelpBadge}>
        <Text style={styles.highlightEmoji}>🔮</Text>
        <Text style={styles.highlightTitle}>Your Theme: {archetype.name}</Text>
        <Text style={styles.highlightSub}>{archetype.traditionalName}{archetype.chinese ? ` (${archetype.chinese})` : ''}</Text>
        <Text style={styles.highlightDesc}>{archetype.description}</Text>
        <ExplainableMetric metricKey="dayMaster" style={styles.dayMasterRow} showIcon={false}>
          <Text style={styles.highlightMeta}>
            {insight.birthPillar.dayMaster.display} · {insight.birthPillar.dayMaster.yinYang} {insight.birthPillar.dayMaster.element} Day Master
          </Text>
        </ExplainableMetric>
      </ExplainableMetric>

      <View style={styles.subHighlight}>
        <ExplainableMetric metricKey="bestUse" style={styles.explainableBlock} iconStyle={styles.inlineHelpBadge}>
          <Text style={styles.subHighlightTitle}>✅ Best Use Today</Text>
          <Text style={styles.subHighlightText}>{archetype.bestUse}</Text>
        </ExplainableMetric>
        <ExplainableMetric metricKey="watchFor" style={styles.explainableBlock} iconStyle={styles.inlineHelpBadge}>
          <Text style={[styles.subHighlightTitle, { marginTop: spacing.sm }]}>⚠️ Watch For</Text>
          <Text style={styles.subHighlightText}>{archetype.watchFor}</Text>
        </ExplainableMetric>
      </View>

      <ExplainableMetric metricKey="personalConnections" style={styles.explainableLabel} helpColor={colors.lightText}>
        <Text style={styles.subsectionLabel}>Your Personal Connections Today</Text>
      </ExplainableMetric>
      {insight.branchInteractions.length > 0 ? insight.branchInteractions.map((interaction) => {
        const color = getQualityColor(interaction.quality);
        return (
          <ExplainableMetric
            key={`${interaction.natalPillar}-${interaction.type}`}
            metricKey="branchInteraction"
            style={[styles.subHighlight, { borderLeftWidth: 3, borderLeftColor: color }]}
            iconStyle={styles.metricHelpBadge}
          >
            <Text style={[styles.subHighlightTitle, { color }]}>
              {interaction.type === 'Combination' ? '🔗' : '⚡'} {interaction.title}
            </Text>
            <Text style={styles.subHighlightText}>{interaction.summary}</Text>
            <Text style={styles.subHighlightText}>{interaction.description}</Text>
          </ExplainableMetric>
        );
      }) : (
        <ExplainableMetric metricKey="steadyBranchEnergy" style={styles.subHighlight} iconStyle={styles.metricHelpBadge}>
          <Text style={styles.subHighlightTitle}>➖ Steady Branch Energy</Text>
          <Text style={styles.subHighlightText}>
            Today makes no direct combination or clash with your natal Year, Month, or Day branches. Let your personal archetype guide the day.
          </Text>
        </ExplainableMetric>
      )}

      <ExplainableMetric metricKey="elementHarmony" style={styles.subHighlight} iconStyle={styles.metricHelpBadge}>
        <Text style={styles.subHighlightTitle}>☯️ Element Harmony: <Text style={{ color: getQualityColor(insight.elementRelationship.quality) }}>{insight.elementRelationship.type}</Text></Text>
        <Text style={styles.subHighlightText}>You ({insight.birthPillar.element})  ⟷  Today ({insight.dayPillar.element} {insight.dayPillar.yinYang})</Text>
        <Text style={styles.subHighlightText}>{insight.elementRelationship.description}</Text>
      </ExplainableMetric>

      <ExplainableMetric metricKey="personalDay" style={styles.highlightBox} iconStyle={styles.metricHelpBadge}>
        <Text style={styles.highlightEmoji}>🔢</Text>
        <Text style={styles.highlightTitle}>Personal Day: {insight.personalDay.personalDay}</Text>
        <Text style={styles.highlightSub}>{insight.personalDay.meaning.theme} · {insight.personalDay.meaning.energy}</Text>
        <Text style={styles.highlightDesc}>{insight.personalDay.meaning.advice}</Text>
        <Text style={styles.highlightMeta}>Color: {insight.personalDay.meaning.color} · Focus: {insight.personalDay.meaning.focus}</Text>
      </ExplainableMetric>

      <ExplainableMetric metricKey="biorhythm" style={styles.explainableLabel} helpColor={colors.lightText}>
        <Text style={styles.subsectionLabel}>Biorhythm · Day {insight.biorhythm.daysAlive.toLocaleString()}</Text>
      </ExplainableMetric>
      {renderBioBar('Physical', insight.biorhythm.physical, 'physicalBiorhythm')}
      {renderBioBar('Emotional', insight.biorhythm.emotional, 'emotionalBiorhythm')}
      {renderBioBar('Mental', insight.biorhythm.intellectual, 'mentalBiorhythm')}
      <ExplainableMetric metricKey="biorhythm" style={styles.explainableAdvice} showIcon={false}>
        <Text style={styles.bioAdviceText}>{insight.biorhythm.advice}</Text>
      </ExplainableMetric>
      <ExplainableMetric metricKey="baziTransit" style={styles.explainableFooter} showIcon={false}>
        <Text style={styles.lunarFooter}>DOB-based Three-Pillar BaZi reading · Birth time not included</Text>
      </ExplainableMetric>

      <View style={styles.divider} />
      <ExplainableMetric metricKey="generalAlmanac" style={styles.explainableLabel} helpColor={colors.lightText}>
        <Text style={styles.subsectionLabel}>Today’s General Almanac · Same for Everyone</Text>
      </ExplainableMetric>
      <ExplainableMetric metricKey="generalAlmanac" style={styles.explainableAdvice} showIcon={false}>
        <Text style={styles.bioAdviceText}>The information below describes the calendar day generally; it is not calculated from your birthday.</Text>
      </ExplainableMetric>

      {renderInfoRow('Day Energy', `${insight.dayPillar.element} ${insight.dayPillar.yinYang} · ${insight.dayPillar.animal}`, insight.elementAdvice.icon, 'dayEnergy')}
      {renderInfoRow('Day Officer', `${insight.dayOfficer.name} (${insight.dayOfficer.chinese})`, '📜', 'dayOfficer')}
      <ExplainableMetric metricKey="dayQuality" style={styles.adviceBox} iconStyle={styles.metricHelpBadge}>
        <View style={[styles.qualityBadge, { backgroundColor: getQualityColor(insight.dayOfficer.quality) + '20' }]}>
          <Text style={[styles.qualityText, { color: getQualityColor(insight.dayOfficer.quality) }]}>
            {insight.dayOfficer.quality.charAt(0).toUpperCase() + insight.dayOfficer.quality.slice(1)}
          </Text>
        </View>
        <Text style={styles.adviceText}>{insight.dayOfficer.advice}</Text>
      </ExplainableMetric>

      {(insight.activities.auspicious.length > 0 || insight.activities.inauspicious.length > 0) && (
        <>
          {insight.activities.auspicious.length > 0 && (
            <ExplainableMetric metricKey="favorableActivities" style={styles.activityRow} iconStyle={styles.inlineHelpBadge}>
              <Text style={styles.activityGood}>✅ Favorable:</Text>
              <Text style={styles.activityList}>{insight.activities.auspicious.join(' · ')}</Text>
            </ExplainableMetric>
          )}
          {insight.activities.inauspicious.length > 0 && (
            <ExplainableMetric metricKey="avoidActivities" style={styles.activityRow} iconStyle={styles.inlineHelpBadge}>
              <Text style={styles.activityBad}>⛔ Avoid:</Text>
              <Text style={styles.activityList}>{insight.activities.inauspicious.join(' · ')}</Text>
            </ExplainableMetric>
          )}
        </>
      )}

      {renderInfoRow('Wealth Direction', insight.luckyDirections.wealth, '💰', 'wealthDirection')}
      {renderInfoRow('Happiness Direction', insight.luckyDirections.happiness, '😊', 'happinessDirection')}
      {renderInfoRow('Fortune Direction', insight.luckyDirections.fortune, '🍀', 'fortuneDirection')}

      {insight.clash ? (
        <ExplainableMetric metricKey="dayClash" style={styles.clashRow} iconStyle={styles.inlineHelpBadge}>
          <Text style={styles.clashText}>⚠️ General day clash: {insight.clash}</Text>
        </ExplainableMetric>
      ) : null}

      <ExplainableMetric metricKey="lunarDate" style={styles.explainableFooter} helpColor={colors.lightText}>
        <Text style={styles.lunarFooter}>
          Lunar: Month {insight.lunarDate.month}, Day {insight.lunarDate.day}
        </Text>
      </ExplainableMetric>
    </View>
  );
};

const createStyles = (colors) => StyleSheet.create({
  content: {
    padding: spacing.md,
    paddingTop: 0,
  },
  highlightBox: {
    backgroundColor: colors.primarySurface,
    borderRadius: layout.borderRadius.medium,
    padding: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.primary + '30',
  },
  highlightEmoji: {
    fontSize: 36,
    marginBottom: 4,
  },
  highlightTitle: {
    fontSize: typography.fontSize.medium,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 2,
    textAlign: 'center',
  },
  highlightSub: {
    fontSize: typography.fontSize.small,
    color: colors.primary,
    marginBottom: 6,
  },
  highlightDesc: {
    fontSize: typography.fontSize.regular,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  highlightMeta: {
    fontSize: typography.fontSize.small,
    color: colors.lightText,
    marginTop: 4,
  },
  subHighlight: {
    backgroundColor: colors.mutedSurface,
    borderRadius: layout.borderRadius.medium,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  subHighlightTitle: {
    fontSize: typography.fontSize.regular,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  subHighlightText: {
    fontSize: typography.fontSize.regular,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  metricHelpBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  inlineHelpBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
  },
  explainableLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
  },
  explainableBlock: {
    position: 'relative',
    paddingRight: 24,
  },
  explainableAdvice: {
    alignSelf: 'stretch',
  },
  explainableFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  dayMasterRow: {
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: typography.fontSize.regular,
    color: colors.lightText,
    flex: 1,
  },
  infoValue: {
    fontSize: typography.fontSize.regular,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'right',
    flex: 1,
  },
  adviceBox: {
    marginTop: 4,
    marginBottom: spacing.sm,
  },
  qualityBadge: {
    alignSelf: 'flex-start',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginBottom: 4,
  },
  qualityText: {
    fontSize: typography.fontSize.small,
    fontWeight: '600',
  },
  adviceText: {
    fontSize: typography.fontSize.regular,
    color: colors.textSecondary,
    lineHeight: 20,
    marginTop: 2,
  },
  activityRow: {
    marginTop: 6,
  },
  activityGood: {
    fontSize: typography.fontSize.small,
    fontWeight: '600',
    color: '#4CAF50',
    marginBottom: 2,
  },
  activityBad: {
    fontSize: typography.fontSize.small,
    fontWeight: '600',
    color: '#F44336',
    marginBottom: 2,
  },
  activityList: {
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.md,
  },
  personalDayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  personalDayCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primarySurface,
    borderWidth: 1,
    borderColor: colors.primary + '30',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  personalDayNum: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.primary,
  },
  personalDayTheme: {
    fontSize: typography.fontSize.medium,
    fontWeight: '600',
    color: colors.text,
  },
  personalDayMeta: {
    fontSize: typography.fontSize.small,
    color: colors.lightText,
  },
  subsectionLabel: {
    fontSize: typography.fontSize.small,
    fontWeight: '600',
    color: colors.lightText,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  bioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  bioLabel: {
    fontSize: typography.fontSize.small,
    color: colors.lightText,
    width: 68,
  },
  bioBarBg: {
    flex: 1,
    height: 10,
    backgroundColor: colors.mutedSurface,
    borderRadius: 5,
    overflow: 'hidden',
    marginHorizontal: 8,
    position: 'relative',
  },
  bioBarFill: {
    height: '100%',
    borderRadius: 5,
  },
  bioBarCenter: {
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: colors.border,
  },
  bioValue: {
    fontSize: typography.fontSize.small,
    fontWeight: '600',
    width: 42,
    textAlign: 'right',
  },
  bioAdviceText: {
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    fontStyle: 'italic',
    marginTop: 4,
  },
  clashRow: {
    backgroundColor: '#FFF3E0',
    borderRadius: layout.borderRadius.medium,
    padding: spacing.sm,
    marginTop: spacing.sm,
  },
  clashText: {
    fontSize: typography.fontSize.small,
    color: '#E65100',
    fontWeight: '500',
  },
  lunarFooter: {
    fontSize: typography.fontSize.small,
    color: colors.lightText,
    textAlign: 'center',
    marginTop: spacing.md,
    fontStyle: 'italic',
  },
  emptyText: {
    fontSize: typography.fontSize.regular,
    color: colors.lightText,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: spacing.md,
  },
});

export default DailyInsight;
